import React from "react";
import styled from "styled-components";

// Branded poster + big play button overlay for the lesson player.
// Renders over the Video.js frame whenever the player is in the
// `idle` (not started) or `ended` state. Click / Space / Enter
// triggers the parent's onPlay callback.
//
// Visual rationale: the play button's conic gradient sweeps through
// the same hues as the Taxsam logo (warm orange → deep orange → blue
// accent → orange) so it visually rhymes with the brand. A radial
// highlight at the top-left gives the disc a subtle 3D feel.

function LessonPosterOverlay({ posterUrl, mode = "idle", onPlay }) {
  // Play / Replay copy + label depend on mode.
  const isReplay = mode === "ended";
  const label = isReplay ? "Tonton Ulang" : "Putar Materi";

  const handleKey = (e) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      onPlay?.();
    }
  };

  return (
    <Overlay
      role="button"
      tabIndex={0}
      aria-label={isReplay ? "Tonton ulang materi" : "Putar materi"}
      onClick={onPlay}
      onKeyDown={handleKey}
      $posterUrl={posterUrl || null}
      $mode={mode}
    >
      <BrandHaze aria-hidden="true" />
      <PlayButton aria-hidden="true">
        {isReplay ? (
          <svg viewBox="0 0 32 32" width="34" height="34">
            <path
              d="M16 6 C10.5 6 6 10.5 6 16 C6 21.5 10.5 26 16 26 C19.6 26 22.7 24 24.5 21"
              fill="none"
              stroke="#fff"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <path d="M22 5 L26 9 L20 12 Z" fill="#fff" />
          </svg>
        ) : (
          <svg viewBox="0 0 32 32" width="32" height="32" aria-hidden="true">
            <path d="M11 7 L25 16 L11 25 Z" fill="#fff" />
          </svg>
        )}
      </PlayButton>
      <Caption>{label}</Caption>
    </Overlay>
  );
}

export default React.memo(LessonPosterOverlay);

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: center;
  justify-content: center;
  z-index: 6;
  cursor: pointer;
  /* Poster image (when available) covers the frame; otherwise the
   * brand haze underneath provides a textured fallback.
   */
  background: ${({ $posterUrl }) =>
    $posterUrl
      ? `linear-gradient(180deg, rgba(0,0,0,0.30) 0%, rgba(0,0,0,0.55) 100%), url(${JSON.stringify($posterUrl)}) center / cover no-repeat`
      : "linear-gradient(135deg, #1f2937 0%, #0f172a 100%)"};
  transition: opacity 220ms ease;
  opacity: ${({ $mode }) => ($mode === "playing" ? 0 : 1)};
  pointer-events: ${({ $mode }) => ($mode === "playing" ? "none" : "auto")};

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: -6px;
  }
`;

const BrandHaze = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(
      ellipse at 25% 20%,
      rgba(239, 135, 38, 0.22) 0%,
      transparent 55%
    ),
    radial-gradient(
      ellipse at 80% 90%,
      rgba(29, 78, 216, 0.18) 0%,
      transparent 60%
    );
  mix-blend-mode: screen;
`;

const PlayButton = styled.div`
  position: relative;
  width: 96px;
  height: 96px;
  border-radius: 50%;
  /* Brand-rhyming gradient: orange → deep orange → blue accent → orange,
   * plus a radial highlight giving the disc a soft 3D feel.
   */
  background:
    radial-gradient(
      circle at 30% 25%,
      rgba(255, 255, 255, 0.3),
      transparent 55%
    ),
    conic-gradient(
      from 200deg at 50% 50%,
      #ef8726 0deg,
      #d85907 90deg,
      #1d4ed8 240deg,
      #ef8726 360deg
    );
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    0 18px 40px rgba(216, 89, 7, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.25),
    inset 0 -3px 8px rgba(0, 0, 0, 0.2);
  transition:
    transform 220ms ease,
    box-shadow 220ms ease;

  & svg {
    /* Triangle visually centred — geometric centre of an equilateral
     * play triangle sits to the left, so nudge right by ~3px.
     */
    margin-left: 4px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.35));
  }

  ${Overlay}:hover &,
  ${Overlay}:focus-visible & {
    transform: scale(1.06);
    box-shadow:
      0 22px 48px rgba(216, 89, 7, 0.6),
      inset 0 1px 0 rgba(255, 255, 255, 0.35),
      inset 0 -3px 8px rgba(0, 0, 0, 0.2);
  }

  ${Overlay}:active & {
    transform: scale(0.98);
  }

  @media screen and (max-width: 720px) {
    width: 72px;
    height: 72px;
    & svg {
      width: 26px;
      height: 26px;
    }
  }
`;

const Caption = styled.div`
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
`;
